/**
 * 手動登録法令管理サービス（実API実装）
 *
 * バックエンドAPI呼び出し:
 * - GET /api/admin/laws - 手動登録法令一覧取得
 * - POST /api/admin/laws - 法令登録
 * - PUT /api/admin/laws/:id - 法令更新
 * - DELETE /api/admin/laws/:id - 法令削除
 * - POST /api/admin/laws/sync - 外部API同期
 */
import apiClient, { extractErrorMessage } from '../../lib/apiClient';
import type { Country } from '../../types';

// ===== 型定義 =====

/**
 * 手動登録法令データ
 */
export interface ManagedLaw {
  id: string;
  title: string;
  lawNumber: string;
  country: Country;
  category: string;
  effectiveDate: string;
  content?: string;
  source: 'manual' | 'api';
  lastUpdated: string;
}

/**
 * 法令作成リクエスト
 */
export interface CreateManagedLawRequest {
  title: string;
  lawNumber: string;
  country: Country;
  category: string;
  effectiveDate: string;
  content?: string;
}

/**
 * 法令更新リクエスト
 */
export interface UpdateManagedLawRequest {
  title?: string;
  lawNumber?: string;
  country?: Country;
  category?: string;
  effectiveDate?: string;
  content?: string;
}

/**
 * API連携ステータス
 */
export interface ApiSyncStatus {
  apiName: string;
  country: string;
  status: 'ok' | 'error' | 'manual';
  lastSync: string | null;
  lawCount: number;
}

/**
 * 同期結果
 */
export interface SyncResult {
  success: boolean;
  message: string;
  syncedApis: {
    apiName: string;
    country: string;
    lawCount: number;
    status: 'ok' | 'error';
  }[];
}

// ===== バックエンドレスポンス型（snake_case） =====

interface BackendManagedLaw {
  id: string;
  title: string;
  law_number: string;
  country: Country;
  category: string;
  effective_date: string;
  content?: string;
  source: 'manual' | 'api';
  last_updated: string;
}

interface BackendApiSyncStatus {
  api_name: string;
  country: string;
  status: 'ok' | 'error' | 'manual';
  last_sync: string | null;
  law_count: number;
}

interface BackendSyncResult {
  success: boolean;
  message: string;
  synced_apis: {
    api_name: string;
    country: string;
    law_count: number;
    status: 'ok' | 'error';
  }[];
}

// ===== 変換関数（snake_case -> camelCase） =====

function convertManagedLaw(law: BackendManagedLaw): ManagedLaw {
  return {
    id: law.id,
    title: law.title,
    lawNumber: law.law_number,
    country: law.country,
    category: law.category,
    effectiveDate: law.effective_date,
    content: law.content,
    source: law.source,
    lastUpdated: law.last_updated,
  };
}

function convertApiSyncStatus(status: BackendApiSyncStatus): ApiSyncStatus {
  return {
    apiName: status.api_name,
    country: status.country,
    status: status.status,
    lastSync: status.last_sync,
    lawCount: status.law_count,
  };
}

function convertSyncResult(result: BackendSyncResult): SyncResult {
  return {
    success: result.success,
    message: result.message,
    syncedApis: result.synced_apis.map((api) => ({
      apiName: api.api_name,
      country: api.country,
      lawCount: api.law_count,
      status: api.status,
    })),
  };
}

// ===== 手動登録法令管理サービス =====

export const managedLawService = {
  /**
   * 手動登録法令一覧取得
   * GET /api/admin/laws
   */
  async getManagedLaws(params?: { country?: Country; search?: string }): Promise<ManagedLaw[]> {
    try {
      const searchParams = new URLSearchParams();
      if (params?.country) searchParams.append('country', params.country);
      if (params?.search) searchParams.append('search', params.search);

      const queryString = searchParams.toString();
      const url = queryString ? `/api/admin/laws?${queryString}` : '/api/admin/laws';

      const response = await apiClient.get<{ data: BackendManagedLaw[] }>(url);
      return response.data.data.map(convertManagedLaw);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * 法令登録
   * POST /api/admin/laws
   */
  async createManagedLaw(data: CreateManagedLawRequest): Promise<ManagedLaw> {
    try {
      const response = await apiClient.post<BackendManagedLaw>('/api/admin/laws', {
        title: data.title,
        law_number: data.lawNumber,
        country: data.country,
        category: data.category,
        effective_date: data.effectiveDate,
        content: data.content,
      });
      return convertManagedLaw(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * 法令更新
   * PUT /api/admin/laws/:id
   */
  async updateManagedLaw(id: string, data: UpdateManagedLawRequest): Promise<ManagedLaw> {
    try {
      const requestData: Record<string, unknown> = {};
      if (data.title !== undefined) requestData.title = data.title;
      if (data.lawNumber !== undefined) requestData.law_number = data.lawNumber;
      if (data.country !== undefined) requestData.country = data.country;
      if (data.category !== undefined) requestData.category = data.category;
      if (data.effectiveDate !== undefined) requestData.effective_date = data.effectiveDate;
      if (data.content !== undefined) requestData.content = data.content;

      const response = await apiClient.put<BackendManagedLaw>(
        `/api/admin/laws/${id}`,
        requestData
      );
      return convertManagedLaw(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * 法令削除
   * DELETE /api/admin/laws/:id
   */
  async deleteManagedLaw(id: string): Promise<void> {
    try {
      await apiClient.delete(`/api/admin/laws/${id}`);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * API連携ステータス取得
   * GET /api/admin/laws/sync-status
   */
  async getSyncStatus(): Promise<ApiSyncStatus[]> {
    try {
      const response = await apiClient.get<{ data: BackendApiSyncStatus[] }>(
        '/api/admin/laws/sync-status'
      );
      return response.data.data.map(convertApiSyncStatus);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * 外部API同期
   * POST /api/admin/laws/sync
   */
  async syncApiLaws(): Promise<SyncResult> {
    try {
      const response = await apiClient.post<BackendSyncResult>('/api/admin/laws/sync');
      return convertSyncResult(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },
};

export default managedLawService;
